// CalendarGrid.jsx - Griglia Calendario
import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import EventCard from './EventCard';

const BADGE_STYLES = {
  "SSE": { bg: "#E2E8F0", border: "#94A3B8" },    // Bordo grigio più definito
  "SSE+TS": { bg: "#E0F2FE", border: "#7DD3FC" },     // Bordo azzurro più marcato
  "TS": { bg: "#FEF3C7", border: "#FCD34D" },  // Bordo ambra più visibile
  "CNV": { bg: "#D1FAE5", border: "#34D399" },
  "default": { bg: "#E2E8F0", border: "#CBD5E1" }
};

const CalendarGrid = ({
  year,
  month,
  events,
  instructors,
  currentUserId,
  isMobile,
  theme,
  onEventClick
}) => {

  // Generazione della griglia del calendario
  const firstDay = new Date(year, month, 1);
  const startDay = (firstDay.getDay() + 6) % 7; // Lunedì = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  const weeks = [];
  for (let w = 0, d = 1; w < 6; w++) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      if ((w === 0 && i < startDay) || d > daysInMonth) {
        week.push(null);
      } else {
        week.push(d++);
      }
    }
    weeks.push(week);
    if (d > daysInMonth) break; // Evita settimane vuote
  }

  // Raggruppamento eventi per data
  const eventsByDate = {};
  events.forEach(ev => {
    const key = ev.start.toDateString();
    if (!eventsByDate[key]) {
      eventsByDate[key] = [];
    }
    eventsByDate[key].push(ev);
  });

  if (isMobile) {
    // Layout Mobile: Lista verticale
    return (
      <Box sx={{ minWidth: '336px' }}>
        {weeks.flatMap(week => week).map((day, idx) => {
          if (!day) return null;
          
          const dateObj = new Date(year, month, day);
          const dateKey = dateObj.toDateString();
          const dayEvents = eventsByDate[dateKey] || [];
          
          return (
            <Box key={"CG_" + idx} 
            onClick={() => onEventClick(dateObj)}
            sx={{ 
              mb: 1, 
              p: 1, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 1,
              bgcolor: 'background.paper'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {day} {dateObj.toLocaleDateString('it-IT', { weekday: 'long' })}
              </Typography>
              
              {dayEvents.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Nessun corso
                </Typography>
              ) : (
                dayEvents.map(event => {
                  const style = BADGE_STYLES[event.tipoLezione] || BADGE_STYLES.default;
                  return(
                  <Box sx={{ position: "relative" }}>
                      {/* BADGE TIPO LEZIONE */}
                      <Box sx={{
                        position: "absolute",
                        top: "-12px",
                        right: -4,
                        background: style.bg,
                        color: "#1E293B",
                        px: 1,
                        py: 0.3,
                        fontSize: "0.7rem",
                        border: `1px solid ${style.border}`,
                        borderRadius: "6px",
                        zIndex: 10,
                        fontWeight: 600,

                      }}>
                        {event.tipoLezione}
                      </Box>
                      <EventCard
                        key={event.id}
                        event={event}
                        instructors={instructors}
                        currentUserId={currentUserId}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(null, event);
                        }}
                        isMobile={true}
                      />
                    </Box>
                );
                }
              )
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  // Layout Desktop: Griglia
  return (
    <Box>
      {weeks.map((week, weekIndex) => (
        <Box
          key={weekIndex}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: 1,
            mb: 1,
          }}
        >
          {week.map((day, dayIndex) => {
            const dateObj = day ? new Date(year, month, day) : null;
            const dateKey = dateObj?.toDateString() || '';
            const dayEvents = eventsByDate[dateKey] || [];
            const isWeekend = dateObj && [0, 6].includes(dateObj.getDay());
            
            return (
              <Paper
                key={dayIndex}
                variant="outlined"
                square
                onClick={() => dateObj && onEventClick(dateObj)}
                sx={{
                  minHeight: { xs: 80, sm: 100 },
                  p: 1,
                  bgcolor: dateObj ? (isWeekend ? '#f8f9fa' : 'inherit') : theme.palette.action.hover,
                  cursor: dateObj ? 'pointer' : 'default',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: dateObj ? 'action.hover' : theme.palette.action.hover,
                  },
                }}
              >
                {day && (
                  <Typography 
                    sx={{ 
                      fontWeight: isWeekend ? 'bold' : 'normal',
                      color: isWeekend ? '#2c5282' : 'inherit',
                      mb: 0.5
                    }}
                  >
                    {day}
                  </Typography>
                )}
                
                {/* Eventi del giorno */}
                {dayEvents.map(event => {
                  const style = BADGE_STYLES[event.tipoLezione] || BADGE_STYLES.default;
                  return (
                  <Box key={event.id} sx={{ position: "relative" }}>

                    {/* BADGE */}
                    <Box sx={{
                      position: "absolute",
                      top: "-12px",
                      right: -4,
                      background: style.bg,  
                      border: `1px solid ${style.border}`, 
                      color: "#1E293B",
                      px: 1.2,
                      py: "2px",
                      fontSize: "0.7rem",
                      borderRadius: "6px",
                      fontWeight: 600,
                      zIndex: 20
                    }}>
                      {event.tipoLezione}
                    </Box>

                    <EventCard
                      event={event}
                      instructors={instructors}
                      currentUserId={currentUserId}
                      isIstruttoreAssegnato={
                        event.istruttore1Id === currentUserId || 
                        event.istruttore2Id === currentUserId
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(null, event);
                      }}
                      isMobile={false}
                    />
                  </Box>);
                }
                )}
              </Paper>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default CalendarGrid;